import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import { CadastroFacialMobile } from './DnaGoFaceRecognition';
import { 
  Plus, Search, Edit, Trash2, Users, Phone, CreditCard, 
  UserCheck, UserX, Loader2, Camera, Shield, Eye, Scan
} from 'lucide-react';

const GRAUS_PARENTESCO = [
  { value: 'pai', label: 'Pai' },
  { value: 'mae', label: 'Mãe' },
  { value: 'avo', label: 'Avô/Avó' },
  { value: 'tio', label: 'Tio/Tia' },
  { value: 'responsavel', label: 'Responsável Legal' },
  { value: 'outro', label: 'Outro' },
];

export default function DnaGoAutorizados() {
  const [autorizados, setAutorizados] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedAutorizado, setSelectedAutorizado] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cadastroFacialOpen, setCadastroFacialOpen] = useState(false);
  const [autorizadoParaFace, setAutorizadoParaFace] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    telefone: '',
    grau_parentesco: 'responsavel',
    foto_url: '',
    alunos_ids: [],
    observacoes: '',
    ativo: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [autRes, alunosRes] = await Promise.all([
        api.get('/dnago/autorizados'),
        api.get('/students')
      ]);
      setAutorizados(autRes.data || []);
      setAlunos(alunosRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/dnago/autorizados/${editingId}`, formData);
        toast.success('Autorizado atualizado!');
      } else {
        await api.post('/dnago/autorizados', formData);
        toast.success('Autorizado cadastrado!');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (autorizado) => {
    setEditingId(autorizado.id);
    setFormData({
      nome: autorizado.nome || '',
      cpf: autorizado.cpf || '',
      telefone: autorizado.telefone || '',
      grau_parentesco: autorizado.grau_parentesco || 'responsavel',
      foto_url: autorizado.foto_url || '',
      alunos_ids: autorizado.alunos_ids || [],
      observacoes: autorizado.observacoes || '',
      ativo: autorizado.ativo !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Deseja desativar este autorizado?')) return;
    try {
      await api.delete(`/dnago/autorizados/${id}`);
      toast.success('Autorizado desativado');
      loadData();
    } catch (error) {
      toast.error('Erro ao desativar');
    }
  };

  const handleView = (autorizado) => {
    setSelectedAutorizado(autorizado);
    setViewDialogOpen(true);
  };

  const handleCadastroFacial = (autorizado) => {
    setAutorizadoParaFace(autorizado);
    setCadastroFacialOpen(true);
  };

  const onFaceCadastrado = () => {
    setCadastroFacialOpen(false);
    setAutorizadoParaFace(null);
    loadData();
    toast.success('Face cadastrada com sucesso!');
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      nome: '',
      cpf: '',
      telefone: '',
      grau_parentesco: 'responsavel',
      foto_url: '',
      alunos_ids: [],
      observacoes: '',
      ativo: true
    });
  };

  const getAlunosNomes = (ids) => {
    return ids.map(id => {
      const aluno = alunos.find(a => a.id === id);
      return aluno?.name || 'N/A';
    }).join(', ');
  };

  const filteredAutorizados = autorizados.filter(a =>
    a.nome?.toLowerCase().includes(search.toLowerCase()) ||
    a.cpf?.includes(search) ||
    a.telefone?.includes(search)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-purple-600" />
            Autorizados
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gerencie pessoas autorizadas a retirar alunos
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Autorizado
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{autorizados.length}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {autorizados.filter(a => a.ativo).length}
                </p>
                <p className="text-xs text-gray-500">Ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Camera className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  {autorizados.filter(a => a.face_template).length}
                </p>
                <p className="text-xs text-gray-500">Com Face</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <UserX className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">
                  {autorizados.filter(a => !a.ativo).length}
                </p>
                <p className="text-xs text-gray-500">Inativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Busca e Tabela */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome, CPF ou telefone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden md:table-cell">CPF</TableHead>
                  <TableHead className="hidden md:table-cell">Telefone</TableHead>
                  <TableHead>Parentesco</TableHead>
                  <TableHead className="hidden lg:table-cell">Alunos</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-28">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAutorizados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      Nenhum autorizado encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAutorizados.map((autorizado) => (
                    <TableRow key={autorizado.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          {autorizado.foto_url ? (
                            <img src={autorizado.foto_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-purple-600 font-semibold">
                              {autorizado.nome?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{autorizado.nome}</TableCell>
                      <TableCell className="hidden md:table-cell font-mono text-sm">
                        {autorizado.cpf || '-'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {autorizado.telefone || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {GRAUS_PARENTESCO.find(g => g.value === autorizado.grau_parentesco)?.label || autorizado.grau_parentesco}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <span className="text-sm text-gray-600">
                          {autorizado.alunos_ids?.length || 0} aluno(s)
                        </span>
                      </TableCell>
                      <TableCell>
                        {autorizado.ativo ? (
                          <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                        ) : (
                          <Badge variant="secondary">Inativo</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleCadastroFacial(autorizado)}
                            title={autorizado.face_template ? "Face cadastrada" : "Cadastrar face"}
                          >
                            <Scan className={`w-4 h-4 ${autorizado.face_template ? 'text-green-500' : 'text-purple-500'}`} />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleView(autorizado)}>
                            <Eye className="w-4 h-4 text-gray-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(autorizado)}>
                            <Edit className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(autorizado.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Cadastro/Edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              {editingId ? 'Editar Autorizado' : 'Novo Autorizado'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da pessoa autorizada a retirar alunos
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Nome Completo *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome completo"
                  required
                />
              </div>

              <div>
                <Label>CPF</Label>
                <Input
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                />
              </div>

              <div>
                <Label>Telefone</Label>
                <Input
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div>
                <Label>Grau de Parentesco</Label>
                <Select
                  value={formData.grau_parentesco}
                  onValueChange={(v) => setFormData({ ...formData, grau_parentesco: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GRAUS_PARENTESCO.map((g) => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>URL da Foto</Label>
                <Input
                  value={formData.foto_url}
                  onChange={(e) => setFormData({ ...formData, foto_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="md:col-span-2">
                <Label>Alunos Vinculados</Label>
                <Select
                  value=""
                  onValueChange={(v) => {
                    if (!formData.alunos_ids.includes(v)) {
                      setFormData({ ...formData, alunos_ids: [...formData.alunos_ids, v] });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione alunos..." />
                  </SelectTrigger>
                  <SelectContent>
                    {alunos.filter(a => !formData.alunos_ids.includes(a.id)).map((aluno) => (
                      <SelectItem key={aluno.id} value={aluno.id}>
                        {aluno.name} - {aluno.class_name || 'Sem turma'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.alunos_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.alunos_ids.map((id) => {
                      const aluno = alunos.find(a => a.id === id);
                      return (
                        <Badge key={id} variant="secondary" className="gap-1">
                          {aluno?.name || id}
                          <button
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              alunos_ids: formData.alunos_ids.filter(i => i !== id)
                            })}
                            className="ml-1 hover:text-red-500"
                          >
                            ×
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <Label>Observações</Label>
                <Textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  placeholder="Observações adicionais..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingId ? 'Salvar' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Visualização */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Autorizado</DialogTitle>
          </DialogHeader>
          {selectedAutorizado && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center">
                  {selectedAutorizado.foto_url ? (
                    <img src={selectedAutorizado.foto_url} alt="" className="w-20 h-20 rounded-full object-cover" />
                  ) : (
                    <span className="text-3xl text-purple-600 font-bold">
                      {selectedAutorizado.nome?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedAutorizado.nome}</h3>
                  <Badge className="mt-1 capitalize">
                    {GRAUS_PARENTESCO.find(g => g.value === selectedAutorizado.grau_parentesco)?.label}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">CPF:</span>
                  <p className="font-mono">{selectedAutorizado.cpf || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Telefone:</span>
                  <p>{selectedAutorizado.telefone || '-'}</p>
                </div>
              </div>

              <div>
                <span className="text-gray-500 text-sm">Alunos Vinculados:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedAutorizado.alunos_ids?.map((id) => {
                    const aluno = alunos.find(a => a.id === id);
                    return (
                      <Badge key={id} variant="outline">
                        {aluno?.name || id}
                      </Badge>
                    );
                  })}
                  {(!selectedAutorizado.alunos_ids || selectedAutorizado.alunos_ids.length === 0) && (
                    <span className="text-gray-400">Nenhum aluno vinculado</span>
                  )}
                </div>
              </div>

              {selectedAutorizado.observacoes && (
                <div>
                  <span className="text-gray-500 text-sm">Observações:</span>
                  <p className="text-sm mt-1">{selectedAutorizado.observacoes}</p>
                </div>
              )}

              <div className="flex items-center gap-2 pt-4 border-t">
                {selectedAutorizado.face_template ? (
                  <Badge className="bg-green-100 text-green-700">
                    <Camera className="w-3 h-3 mr-1" /> Face Cadastrada
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    <Camera className="w-3 h-3 mr-1" /> Sem Face
                  </Badge>
                )}
                {selectedAutorizado.ativo ? (
                  <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                ) : (
                  <Badge variant="destructive">Inativo</Badge>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cadastro Facial */}
      {cadastroFacialOpen && autorizadoParaFace && (
        <CadastroFacialMobile
          autorizadoId={autorizadoParaFace.id}
          autorizadoNome={autorizadoParaFace.nome}
          onSuccess={onFaceCadastrado}
          onClose={() => {
            setCadastroFacialOpen(false);
            setAutorizadoParaFace(null);
          }}
        />
      )}
    </div>
  );
}
